import DropdownSelector from "@/components/dropdown-selector/DropdownSelector";
import { FC, useEffect, useState } from "react";
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import FullScreenActivityFrame from '../components/activity-frame/full-screen-activity-spinner';
import { useDialog } from "../components/dialog/DialogContext";
import TableTab from "../components/tabs/table-tab";
import { FILTER_COMMAND, MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../constants/command-status";
import Commands from "../constants/commands";
import DeviceStatus, { DEVICE_STATES2 } from "../constants/device-status";
import { LICENSE_MDRSE_HID, LICENSE_MDRSE_STID, LICENSE_MDRSE_STID_HID } from "../constants/license-constants";
import { HARDWARE_READERS } from "../constants/menu-types";
import { HardwareSubCmds } from "../constants/sub-commands";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { useIcaWebsocketContext } from "../contexts/websocket/IcaWebsocketContext";
import { exportLicenseToXMLFormat, getArrayBufferFromBlob } from "../exporters/LicenseHelper";
import closeIcon from '../images/close.svg';
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/ReaderColumns';
import { ExecuteCommand } from "../models/ExecuteCommand";
import { ListData } from "../models/ListData";
import { ListQuery } from "../models/ListQuery";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import { Reader } from "../models/Reader";
import ControllerSearch from "../models/search/ControllerSearch";
import DoorSearch from "../models/search/DoorSearch";
import searchColumns from '../models/search/ReaderSearch';
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getDeviceTableLegendItems } from "../models/TableLegend";
import { getControllerNames } from "../services/controllerService";
import { getDoorNamesWithPagination } from "../services/doorService";
import { executeCommandForDevice } from "../services/executeCommandService";
import { exportBundledCredentialRequest, exportMobileReaderLicense, getReaders, getReadersById, importReaderLicense } from "../services/readerService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { getErrorMessage } from "../services/utilities/ServiceError";
import { IAppState } from "../store/types";
import { getCommand, getDeviceStates, getWsDevice } from "../utilities/WebsocketUtil";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    setBusyMessage: (message: string) => void,
}

const ReaderTab: FC<Props> = ({ setBusy, categories, setCategories, setBusyMessage }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.readers);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const itemPath = '/access-control/hardware/reader/';
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [updateTable, setUpdateTable] = useState(() => (updateMethod: (tableData: any[]) => any[]) => { return });
    const [busyDownloading, setBusyDownloading] = useState<boolean>(false);
    const [readersStatusInfo, setReadersStatusInfo] = useState<{ controllerFixedAddress: string, readerFixedAddresses: string[] }>({
        readerFixedAddresses: [],
        controllerFixedAddress: ""
    });
    const dialog = useDialog();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isOpen, setIsOpen] = useState<boolean>();
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    const tableDataUpdate = (updateTableMethod: (updateMethod: (tableData: any[]) => any[]) => void) => {
        setUpdateTable(() => updateTableMethod);
    }
    const ID_EXPORT_BUNDLED_CREDENTIALS = "99";
    const ID_IMPORT_LICENSE = "0";
    const licenseOptions = [
        { value: LICENSE_MDRSE_STID, label: translate.byKey(TranslationKeys.exportSTid) },
        { value: LICENSE_MDRSE_HID, label: translate.byKey(TranslationKeys.exportHID) },
        { value: LICENSE_MDRSE_STID_HID, label: translate.byKey(TranslationKeys.exportSTidAndHID) },
        { value: ID_EXPORT_BUNDLED_CREDENTIALS, label: translate.byKey(TranslationKeys.exportBundledCredentialRequest) },
        { value: ID_IMPORT_LICENSE, label: translate.byKey(TranslationKeys.importLicense) }
    ];

    // The device status codes this view is interesting in
    const filterStatusCodes = [DEVICE_STATES2.PERIPHERAL_STATE];

    useEffect(() => {
        updateTable((tableData) => {
            return updateReaderState(readersStatusInfo.readerFixedAddresses, tableData, readersStatusInfo.controllerFixedAddress)
        })
    }, [readersStatusInfo])

   
    useEffect(() => {
        setLookup([{
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'controller.deviceImprox.id',
            values: [],
            dataMethod: getControllerNames,
            searchAccessor: ControllerSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'location.id',
            values: [],
            dataMethod: getDoorNamesWithPagination,
            searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName
        }]);
    }, [])

    const onDelete = async (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        setBusyMessage(translate.byKey(TranslationKeys.unstallingMessage))

        let fixedAddress: string | undefined;

        try {
            const reader = await getReadersById(id);
            fixedAddress = reader.fixedAddress
        } catch (err) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.deviceNotFound) })
        }
        const command: ExecuteCommand = {
            cmd: Commands.Engine.label,
            cmdSubType: HardwareSubCmds.Uninstall_Reader,
            userLoginId: user.id,
            fixedAddress: fixedAddress,
            jsonResponse: ""
        }

        return new Promise((resolve, reject) => {
            executeCommandForDevice(command)
                .then(
                    (deleteResult: any) => {
                        resolve(deleteResult);
                        const newCatergories: Category[] = categories.map((category) => {
                            return category.id === 2 ? { ...category, count: (category?.count ?? 1) - 1 } : category;
                        })
                        setCategories(newCatergories);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    }).catch(error => {
                        dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
                    })
        });
    }

    const icaWebSocket = useIcaWebsocketContext();
    let filterStatusCodesRequestTimeOut;

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            sendfilterStatuCodeRequest();
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage)
        }

        return () => {
            clearTimeout(filterStatusCodesRequestTimeOut);
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DEVICE, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
    }, [icaWebSocket.connectionState])

    const sendfilterStatuCodeRequest = () => {
        // Tell the server to only report on these device status codes.
        const filterStatusCodesRequest = {
            "command": MONITORING_COMMAND.MONITORING_DEVICE,
            "attributes": {
                "name": FILTER_COMMAND.FILTER,
                "statusCodeFilters": {
                    "includeStatusCodes": filterStatusCodes
                }
            }
        }

        filterStatusCodesRequestTimeOut = setTimeout(() => {
            icaWebSocket.sendMessage(JSON.stringify(filterStatusCodesRequest))
        }, 5000)
    }

    const onMessage = (data) => {
        if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
            const response = JSON.parse(data.data);
            if (getCommand(response) === MONITORING_COMMAND.MONITORING_DEVICE) {
                const deviceStates = getDeviceStates(response);

                if (Array.isArray(deviceStates)) {
                    deviceStates.forEach((deviceState) => {
                        const wsDevice = getWsDevice(deviceState);

                        // Peripherals (online readers attached to the controller)
                        if (wsDevice.fixedAddress) {
                            const readerFixedAddresses = wsDevice.peripheralAddresses ? wsDevice.peripheralAddresses : [];
                            setReadersStatusInfo({
                                readerFixedAddresses: readerFixedAddresses,
                                controllerFixedAddress: wsDevice.fixedAddress
                            })
                        }
                    });
                }
            }
        }
    }

    const updateReaderState = (readerFixedAddresses: string[], readers: Reader[], controllerFixedAddress: String) => {
        const readersForThisController = readers.filter((reader: Reader) => reader.controllerAddress === controllerFixedAddress);

        readersForThisController.forEach((reader: Reader) => {
            let status = DeviceStatus.Unknown.id;

            if (readerFixedAddresses.includes(reader.fixedAddress ?? '')) {
                status = DeviceStatus.Online.id;
            }
            else if (!readerFixedAddresses.includes(reader.controllerAddress ?? '')) {
                status = DeviceStatus.Offline.id;
            }
            else if (!reader.enabled) {
                status = DeviceStatus.Disabled.id;
            }

            // Update the status of the reader if it has changed
            if (reader.status !== status) {
                const readerToUpdate = readers.find((r: Reader) => r.id === reader.id);
                if (readerToUpdate) {
                    readerToUpdate.status = status;
                }
            }
        });

        return readers;
    }

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))

    const initialiseUserPreferences = (setDetails: (userLoginId, HARDWARE_READERS) => void) => {
        setDetails(userLoginId, HARDWARE_READERS);
    }
    // <-- end context

    const onLicenseOptionSelected = (option: any) => {

        // Import License
        if (option.value === ID_IMPORT_LICENSE) {
            setBusyDownloading(false);
            setIsOpen(true);
            return;
        }

        setBusyDownloading(true);

        // Export Bundled Credential Request
        if (option.value === ID_EXPORT_BUNDLED_CREDENTIALS) { // 99 is a made-up number. Can be anything that matches the value in the options.
            exportBundledCredentialRequest(translate).then((response) => {
                exportLicenseToXMLFormat(response);
                setBusyDownloading(false);
            }).catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
                setBusyDownloading(false);
            });
            return;
        }

        // No devices selected to export license for
        if (selectedIds.length === 0) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.noDeviceSelected), message: translate.byKey(TranslationKeys.selectAtLeastOneDevice) });
            setBusyDownloading(false);
            return;
        }

        // Export License
        exportMobileReaderLicense(selectedIds.join(","), option.value, translate).then((response) => {
            exportLicenseToXMLFormat(response);
            setBusyDownloading(false);
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            setBusyDownloading(false);
        });
    }

    const importLicense = async (event) => {
        try {
            if (event.length > 0 && event[0].name != null && event[0].size != 0 && (event[0].name.indexOf('.lic') !== -1 || event[0].name.indexOf('.conf') !== -1)) {
                const convertedData = Object.values(await getArrayBufferFromBlob(event[0]));
                await importReaderLicense(convertedData);
                dataRefresh((listParameters?: ListQuery) => getReaders(translate, listParameters))
                toast.success(translate.byKey(TranslationKeys.licenseImportedSuccessfully));
                return;
            }

            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.noFileSelected) })
            return;
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
        } finally {
            setBusy(false);
            setIsOpen(false);
        }
    }

    return <>
        {busyDownloading ? <FullScreenActivityFrame message={translate.byKey(TranslationKeys.preparingLicenseRequestForDownload)} /> : null}
        <TableContextProvider
            initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
            dataMethod={(listParameters?: ListQuery) => getReaders(translate, listParameters)}
            deleteMethod={onDelete}
            legendItems={getDeviceTableLegendItems(translate)}
            tableDescriptor={columns}
            searchDescriptor={searchColumns}
            useTable
            useLegend
            useSearch
            useFilter
            usePagination
            useSort
            tableDataUpdate={tableDataUpdate}
            lookup={lookup}
            manualDataRefresh={manualDataRefresh}>
            <TableTab
                hideAddNewButton={true}
                title={title}
                selectRows={true}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                rowPath={itemPath} 
                cyName="readers">
                <DropdownSelector className="flex px-4 rounded-full shadow text-white bg-sidemenu-focus text-sm items-center mr-2" buttonClassName="flex px-8 items-center"
                    onSelected={(option) => onLicenseOptionSelected(option)}
                    options={licenseOptions}>
                    {translate.byKey(TranslationKeys.license)}
                </DropdownSelector>
            </TableTab>
        </TableContextProvider>
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
            className="bg-white border-none m-auto shadow select-none outline-none w-content">
            <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                <div className="flex flex-row">
                    <p className="pl-3 font-bold font text-white">{translate.byKey(TranslationKeys.importLicense)}</p>
                </div>
                <img src={closeIcon} onClick={() => setIsOpen(false)} className="cursor-pointer mr-2" />
            </div>
            <div className="container m-4">
                <input
                    type="file"
                    accept=".lic"
                    onChange={(event) => importLicense(event.target.files)} />
            </div>
        </Modal>
    </>
}
export default ReaderTab;
